import PropTypes from 'prop-types';
/**
 * The `hoverElement` relatively to a hoverable element
 * Used by components like DropdownButton and Tooltip.
 */
import React from 'react';
import CSSTransitionGroup from 'react-transition-group/CSSTransitionGroup';
import $ from 'jquery';
import debounce from 'lodash.debounce';
import classNames from 'classnames';
import DetachableElement from './DetachableElement';

// TODO: propose publishing `element-position` to npm
import { positionElementWithAutoAdjustment, getPossiblePositions } from '../lib/elementPosition';

import { getComponentClasses } from '@99designs/utils/app';

export default class HoverCard extends React.Component {
    constructor(props) {
        super(props);
        this.updatePosition = this.updatePosition.bind(this);

        this.contentNode = null;
        this.triangleNode = null;
        this.cardNode = null;
    }

    componentDidUpdate() {
        if (this.props.isActive) {
            this.props.onShow(this.contentNode);
        } else {
            this.props.onHide();
        }

        this.updatePosition();
    }

    componentWillUnmount() {
        window.removeEventListener('scroll', this.debouncedUpdatePositionFunc, true);
    }

    componentDidMount() {
        this.updatePosition();

        if (!this.debouncedUpdatePositionFunc) {
            this.debouncedUpdatePositionFunc = debounce(this.updatePosition, 50);
        }

        window.addEventListener('scroll', this.debouncedUpdatePositionFunc, true);
    }

    updatePosition() {
        var $anchorElement = $(this.props.anchorNodeSelector);
        var $cardElement = $(this.cardNode);
        var $triangleElement = $(this.triangleNode);
        var cardPosition;

        if (
            this.props.isActive &&
            $anchorElement.length > 0 &&
            this.triangleNode &&
            this.cardNode
        ) {
            cardPosition = this.positionCard(
                $cardElement,
                $anchorElement,
                $triangleElement,
                this.props.hoverPosition
            );
            this.positionTriangle($triangleElement, $anchorElement, $cardElement, cardPosition);
            this.setTransformOrigin($cardElement, $triangleElement);
        }
    }

    getPercentageString(part, total) {
        return total > 0 ? Math.round(part / total * 100) + '%' : '0%';
    }

    positionCard($cardElement, $anchorElement, $triangleElement, position) {
        var triangleRect = $triangleElement.get(0).getBoundingClientRect();

        return positionElementWithAutoAdjustment(
            $cardElement,
            $anchorElement,
            position,
            this.props.distance + triangleRect.height / 2,
            this.props.alignmentOffset
        );
    }

    positionTriangle($triangleElement, $anchorElement, $cardElement, cardPosition) {
        var positionDirection = cardPosition.split('-').shift();
        var triangleDestinationPosition = {};
        var anchorRect = $anchorElement.get(0).getBoundingClientRect();
        var cardRect = $cardElement.get(0).getBoundingClientRect();
        var triangleRect = $triangleElement.get(0).getBoundingClientRect();
        var triangleDestinationPageOffsetX =
            anchorRect.left + anchorRect.width / 2 - triangleRect.width / 2;
        var triangleDestinationPageOffsetY =
            anchorRect.top + anchorRect.height / 2 - triangleRect.height / 2;

        if (['down', 'up'].indexOf(positionDirection) > -1) {
            if (cardRect.width >= anchorRect.width) {
                triangleDestinationPosition['left'] =
                    triangleDestinationPageOffsetX - cardRect.left;
            } else {
                triangleDestinationPosition['left'] = (cardRect.width - triangleRect.width) / 2;
            }
        }

        if (['left', 'right'].indexOf(positionDirection) > -1) {
            if (cardRect.height >= anchorRect.height) {
                triangleDestinationPosition['top'] = triangleDestinationPageOffsetY - cardRect.top;
            } else {
                triangleDestinationPosition['top'] = (cardRect.height - triangleRect.height) / 2;
            }
        }

        $triangleElement.css(triangleDestinationPosition);
        $triangleElement.removeClass();
        $triangleElement.addClass('hover-card__triangle');
        $triangleElement.addClass('hover-card__triangle--' + positionDirection);

        return triangleDestinationPosition;
    }

    setTransformOrigin($cardElement, $triangleElement) {
        var trianglePosition = $triangleElement.position();
        var cardRect = $cardElement.get(0).getBoundingClientRect();
        var transformOrigin = {};

        if (this.props.hasAnimation) {
            transformOrigin['transform-origin-x'] = this.getPercentageString(
                trianglePosition.left,
                cardRect.width
            );
            transformOrigin['transform-origin-y'] = this.getPercentageString(
                trianglePosition.top,
                cardRect.height
            );
            $cardElement.css(transformOrigin);
        }
    }

    render() {
        var hoverCardClassNames = classNames(
            getComponentClasses('hover-card', this.props.variations),
            {
                'hover-card--with-transition': this.props.hasTransition,
                'hover-card--with-animation': this.props.hasAnimation,
                'hover-card--tooltip': this.props.type === 'tooltip',
                'hover-card--dropdown': this.props.type === 'dropdown',
                'hover-card--tour': this.props.type === 'tour',
            }
        );

        if (this.props.isActive) {
            return (
                <DetachableElement>
                    <CSSTransitionGroup
                        transitionName="hover-card-"
                        transitionEnter={false}
                        transitionLeave={false}
                        transitionAppear={this.props.hasAnimation || this.props.hasTransition}
                        transitionAppearTimeout={this.props.hasAnimation ? 300 : 200}
                    >
                        <div className={hoverCardClassNames} ref={n => (this.cardNode = n)}>
                            <div className="hover-card__content" ref={n => (this.contentNode = n)}>
                                {this.props.children}
                            </div>
                            <div
                                className="hover-card__triangle"
                                ref={n => (this.triangleNode = n)}
                            />
                        </div>
                    </CSSTransitionGroup>
                </DetachableElement>
            );
        } else {
            return null;
        }
    }
}

HoverCard.propTypes = {
    anchorNodeSelector: PropTypes.string.isRequired,
    alignmentOffset: PropTypes.number,
    distance: PropTypes.number,
    hasTransition: PropTypes.bool,
    hasAnimation: PropTypes.bool,
    hoverPosition: PropTypes.oneOf(getPossiblePositions()),
    isActive: PropTypes.bool,
    type: PropTypes.oneOf(['tooltip', 'dropdown', 'tour']),
    onShow: PropTypes.func,
    onHide: PropTypes.func,
    variations: PropTypes.array,
    children: PropTypes.node,
};

HoverCard.defaultProps = {
    alignmentOffset: 0,
    distance: 3,
    hasTransition: false,
    hasAnimation: false,
    hoverPosition: 'down-center',
    isActive: false,
    type: 'tooltip',
    onShow: function() {},
    onHide: function() {},
    variations: [],
    children: '',
};



// WEBPACK FOOTER //
// ./~/@99designs/react-components/components/HoverCard.jsx